import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AssistantRuntimeProvider, ComposerPrimitive, ThreadPrimitive, useLocalRuntime, useAssistantState } from "@assistant-ui/react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { getAiChatStreamResponse } from "../../services/aiChatService";
import { searchHasOfferUsers } from "../../services/usersService";

// A simple status component to show when the assistant is thinking
const AssistantLoadingStatus = () => {
    const isRunning = useAssistantState((state) => state.thread.isRunning);
    const messages = useAssistantState((state) => state.thread.messages);
    const lastMessage = messages[messages.length - 1];

    // Hide loading if the last message is from assistant and has content (streaming started)
    const isStreaming = lastMessage?.role === 'assistant' && (lastMessage.content?.length > 0 || lastMessage.text?.length > 0);

    if (!isRunning || isStreaming) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, ml: 2, mb: 2, bgcolor: '#f0f2f5', borderRadius: 3, width: 'fit-content' }}>
            <CircularProgress size={16} thickness={5} sx={{ color: '#666' }} />
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                InfluioGPT is thinking...
            </Typography>
        </Box>
    );
};

// Helper to format message content (Bold and Links)
const formatMessageContent = (text, role) => {
    if (!text) return text;

    // Split by URLs first
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
        if (part.match(urlRegex)) {
            const linkColor = role === 'user' ? '#fff' : '#1976d2';

            // Check if it is an excel file
            if (part.includes('.xlsx') || part.includes('.xls')) {
                let fileName = "Excel Report";
                try {
                    const urlObj = new URL(part);
                    fileName = decodeURIComponent(urlObj.pathname.split('/').pop());
                } catch (e) {
                    // fallback
                }

                return (
                    <a
                        key={`link-${index}`}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: linkColor,
                            textDecoration: 'none',
                            fontWeight: '600',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: role === 'user' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            marginTop: '4px',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        <span style={{ fontSize: '1.2em' }}>📊</span> {fileName}
                    </a>
                );
            }

            return (
                <a
                    key={`link-${index}`}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: linkColor, textDecoration: 'underline', wordBreak: 'break-all', fontWeight: '500' }}
                >
                    {part}
                </a>
            );
        }

        // Then parse markdown bold (**text**)
        const boldParts = part.split(/(\*\*[^*]+\*\*)/g);
        return boldParts.map((boldPart, boldIndex) => {
            if (boldPart.startsWith('**') && boldPart.endsWith('**')) {
                const innerText = boldPart.slice(2, -2);
                return <strong key={`bold-${index}-${boldIndex}`}>{innerText}</strong>;
            }
            return <span key={`text-${index}-${boldIndex}`}>{boldPart}</span>;
        });
    });
};

const ChatMessage = ({ message }) => {
    // Reverting to props since we are manually controlling the loop
    const content = message.content;

    // console.log('UI Rendering Manual Message:', message.id, 'Role:', message.role);

    let contentText = "";
    if (content) {
        if (Array.isArray(content)) {
            contentText = content.map(c => c.text || '').join('');
        } else if (typeof content === 'string') {
            contentText = content;
        } else if (content.text) {
            contentText = content.text;
        }
    } else {
        contentText = message.text || '';
    }

    if (!contentText && message.role === 'assistant') {
        return null; // likely loading
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
            mb: 2
        }}>
            <Box sx={{
                maxWidth: '80%',
                p: 2,
                borderRadius: 3,
                bgcolor: message.role === 'user' ? '#1976d2' : '#f0f2f5',
                color: message.role === 'user' ? 'white' : 'black',
                boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
            }}>
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '14px', lineHeight: 1.7 }}>
                    {formatMessageContent(contentText, message.role)}
                </Typography>
            </Box>
        </Box>
    );
};

const ManualMessageList = ({ messages: propMessages }) => {
    const runtimeMessages = useAssistantState((state) => state.thread.messages);

    // If we have prop messages (historical), merge them with any new runtime messages
    // Runtime messages that are NEW (not in propMessages) should be appended
    const messages = React.useMemo(() => {
        if (!propMessages || propMessages.length === 0) {
            return runtimeMessages;
        }

        // Get IDs of prop messages to avoid duplicates
        const propMessageIds = new Set(propMessages.map(m => m.id));

        // Find runtime messages that aren't in the prop messages (new messages)
        const newRuntimeMessages = runtimeMessages.filter(m => !propMessageIds.has(m.id));

        // Merge: historical messages + new runtime messages
        return [...propMessages, ...newRuntimeMessages];
    }, [propMessages, runtimeMessages]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {messages.map((msg) => (
                <ChatMessage key={msg.id} message={msg} />
            ))}
        </Box>
    );
};

const ThreadStateLogger = () => {
    const messages = useAssistantState((state) => state.thread.messages);
    console.log('Current Thread Messages Count:', messages.length);
    return null;
};

const ActiveChatSession = ({ conversationId, initialMessages, readOnly = false }) => {
    // Transform initial messages to ensure content is in the correct format for assistant-ui
    const normalizedMessages = [];
    (initialMessages || []).forEach((msg, index) => {
        const content = typeof msg.content === 'string'
            ? [{ type: "text", text: msg.content }]
            : Array.isArray(msg.content) ? msg.content : [{ type: "text", text: String(msg.content || "") }];

        const id = msg.id || `msg-${conversationId}-${index}`;
        // Important: parentId should be undefined for the first message, not null
        const parentId = index > 0 ? normalizedMessages[index - 1].id : undefined;

        normalizedMessages.push({
            id,
            role: msg.role || 'user',
            content,
            parentId,
        });
    });

    const runtime = useLocalRuntime({
        initialMessages: [],
        async *run({ messages }) {
            setInputValue(""); // Clear input after sending

            const userMessage = messages[messages.length - 1];
            // Safe access to text content
            const messageContent = userMessage.content[0]?.text || "";
            console.log('Starting AI Chat run with input:', messageContent);

            try {
                // Pass conversationId and full message history (including the new user message)
                const response = await getAiChatStreamResponse(conversationId, messages);

                if (!response.ok) {
                    console.error('API Error Response:', response.status, response.statusText);
                    throw new Error(`Failed to fetch from AI API: ${response.statusText}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let accumulatedText = "";
                let buffer = "";

                // Generate a stable ID for this stream response
                const responseId = `assistant-response-${Date.now()}`;

                console.log('Start reading stream...');

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        console.log('Stream finished. Final text:', accumulatedText);
                        break;
                    }

                    const chunk = decoder.decode(value, { stream: true });
                    buffer += chunk;

                    // Split the buffer by newlines
                    const lines = buffer.split('\n');

                    // Keep the last part in the buffer
                    buffer = lines.pop();

                    for (const line of lines) {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) continue;

                        console.log('Processing line:', trimmedLine);

                        let contentToYield = '';

                        // Handle "data:" or "data: "
                        if (trimmedLine.startsWith('data:')) {
                            // Extract content after "data:"
                            const dataContent = trimmedLine.substring(5).trim();
                            if (dataContent === '[DONE]') {
                                console.log('Stream DONE signal received');
                                continue;
                            }

                            try {
                                const json = JSON.parse(dataContent);
                                contentToYield = json.content || json.text || json.message || '';
                                console.log('Parsed JSON content:', contentToYield);
                            } catch (e) {
                                contentToYield = dataContent;
                                console.log('Failed to parse JSON, using raw:', contentToYield);
                            }
                        } else {
                            // Try parsing raw line as JSON
                            try {
                                const json = JSON.parse(trimmedLine);
                                contentToYield = json.content || json.text || json.message || '';
                                console.log('Parsed raw JSON:', contentToYield);
                            } catch (e) {
                                contentToYield = trimmedLine;
                                console.log('Using raw line:', contentToYield);
                            }
                        }

                        if (contentToYield) {
                            if (contentToYield.startsWith('"') && contentToYield.endsWith('"')) {
                                contentToYield = contentToYield.slice(1, -1);
                            }
                            contentToYield = contentToYield.replace(/\\n/g, '\n');

                            accumulatedText += contentToYield;

                            yield {
                                id: responseId,
                                parentId: userMessage.id, // Linking to user message is critical
                                role: "assistant",
                                content: [{ type: "text", text: accumulatedText }],
                            };
                        }
                    }
                }
            } catch (error) {
                console.error('Streaming error detail:', error);
                const fallbackMsg = "I'm sorry, I'm having trouble connecting to the AI brain right now. Please check if the API is configured correctly.";
                yield {
                    id: `error-${Date.now()}`,
                    role: "assistant", // Explicitly setting role
                    content: [{ type: "text", text: fallbackMsg }],
                };
            }
        },
    });

    const offers = useSelector((state) => state.offersList?.offers || []);
    const [inputValue, setInputValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);
    const [triggerChar, setTriggerChar] = useState(null); // '#' or '@'
    const [highlightedIndex, setHighlightedIndex] = useState(0); // For keyboard navigation

    // Track the cursor position and the partial query
    const [query, setQuery] = useState("");

    // Keyboard navigation handler
    const handleKeyDown = (e) => {
        // Only handle keyboard navigation when dropdown is showing with items
        if (!showSuggestions || filteredItems.length === 0) {
            // Let Enter propagate for form submission
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredItems.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredItems.length - 1
                );
                break;
            case 'Enter':
            case 'Tab':
                // Only prevent default and select item when dropdown is visible
                e.preventDefault();
                handleSelectItem(filteredItems[highlightedIndex]);
                break;
            case 'Escape':
                setShowSuggestions(false);
                setTriggerChar(null);
                break;
            default:
                break;
        }
    };

    // Reset highlighted index when suggestions change
    React.useEffect(() => {
        setHighlightedIndex(0);
    }, [filteredItems]);

    // We attach this to the Input's onChange
    const handleInputChange = (e) => {
        const val = e.target.value;
        setInputValue(val);

        const cursorPosition = e.target.selectionStart;
        const textBeforeCursor = val.slice(0, cursorPosition);

        // Check for # or @ symbol before cursor
        const lastHashIndex = textBeforeCursor.lastIndexOf('#');
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        // Determine which one is more recent (closer to cursor)
        const lastTriggerIndex = Math.max(lastHashIndex, lastAtIndex);

        if (lastTriggerIndex !== -1) {
            const char = textBeforeCursor[lastTriggerIndex];
            const textAfterTrigger = textBeforeCursor.slice(lastTriggerIndex + 1);

            // Simple rule: only trigger if no spaces in the search query yet (except for @ mentions which support spaces)
            if (char === '@' || !textAfterTrigger.includes(' ')) {
                setQuery(textAfterTrigger);
                setTriggerChar(char);
                // setShowSuggestions handled in useEffect for influencers
                if (char === '#') setShowSuggestions(true);
                return;
            }
        }

        setShowSuggestions(false);
        setTriggerChar(null);
    };

    useEffect(() => {
        const timer = setTimeout(async () => {
            if (showSuggestions || (triggerChar === '@' && query?.length >= 2)) {
                const lowerQuery = query.toLowerCase();

                if (triggerChar === '#') {
                    const filtered = offers.filter(offer =>
                        (offer.name || "").toLowerCase().includes(lowerQuery) ||
                        String(offer.offerId).includes(lowerQuery)
                    );
                    setFilteredItems(filtered);
                    setShowSuggestions(true);
                } else if (triggerChar === '@' && query?.length >= 2) {
                    try {
                        const response = await searchHasOfferUsers({ query });
                        if (response.data) {
                            // Backend returns a simple array of users
                            setFilteredItems(response.data);
                            setShowSuggestions(response.data.length > 0);
                        } else {
                            setFilteredItems([]);
                            setShowSuggestions(false);
                        }
                    } catch (error) {
                        console.error("Influencer search failed:", error);
                        setFilteredItems([]);
                        setShowSuggestions(false);
                    }
                }
            } else {
                setFilteredItems([]);
                if (triggerChar !== '@' || query?.length < 2) setShowSuggestions(false);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timer);
    }, [query, triggerChar, offers, showSuggestions]);

    const handleSelectItem = (item) => {
        const cursorPosition = document.activeElement.selectionStart || inputValue.length;
        const textBeforeCursor = inputValue.slice(0, cursorPosition);
        const lastTriggerIndex = textBeforeCursor.lastIndexOf(triggerChar);

        if (lastTriggerIndex !== -1) {
            const prefix = inputValue.slice(0, lastTriggerIndex);
            const suffix = inputValue.slice(cursorPosition);

            let insertedText = "";
            if (triggerChar === '#') {
                insertedText = `#${item.name}-${item.offerId} `;
            } else if (triggerChar === '@') {
                insertedText = `@${item.fullname}-${item.hasoffersid} `;
            }

            const newText = `${prefix}${insertedText}${suffix}`;
            const newCursorPosition = prefix.length + insertedText.length;

            setInputValue(newText);
            setShowSuggestions(false);
            setTriggerChar(null);

            // Allow state update then refocus and set cursor position
            setTimeout(() => {
                const input = document.querySelector('textarea, input[type="text"]');
                if (input) {
                    input.focus();
                    input.setSelectionRange(newCursorPosition, newCursorPosition);
                }
            }, 0);
        }
    };

    return (
        <AssistantRuntimeProvider runtime={runtime}>
            <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative' // For absolute positioning of suggestions
            }}>
                <ThreadPrimitive.Root style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ThreadPrimitive.Viewport style={{ flexGrow: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <ThreadPrimitive.Empty>
                            <Box sx={{ textAlign: 'center', mt: 10 }}>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>InfluioGPT</Typography>
                                <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>Start a conversation to unlock insights</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{
                                            backgroundColor: '#1976d2',
                                            color: 'white',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            fontWeight: 'bold',
                                            fontSize: '18px',
                                            fontFamily: "'Inter', sans-serif"
                                        }}>@</Box>
                                        <Typography variant="body2" sx={{ fontFamily: "'Inter', sans-serif", color: '#555' }}>
                                            Mention influencer
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{
                                            backgroundColor: '#9c27b0',
                                            color: 'white',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            fontWeight: 'bold',
                                            fontSize: '18px',
                                            fontFamily: "'Inter', sans-serif"
                                        }}>#</Box>
                                        <Typography variant="body2" sx={{ fontFamily: "'Inter', sans-serif", color: '#555' }}>
                                            Reference offer
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </ThreadPrimitive.Empty>
                        <ThreadStateLogger />

                        {/* Manual Message Loop to debug rendering */}
                        <ManualMessageList messages={readOnly ? normalizedMessages : undefined} />

                        <AssistantLoadingStatus />
                    </ThreadPrimitive.Viewport>

                    {/* Suggestions Popover */}
                    {showSuggestions && filteredItems.length > 0 && (
                        <ClickAwayListener onClickAway={() => {
                            setShowSuggestions(false);
                            setTriggerChar(null);
                        }}>
                            <Paper sx={{
                                position: 'absolute',
                                bottom: '80px', // Just above the input area
                                left: '20px',
                                width: '300px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: 3
                            }}>
                                <List dense>
                                    {filteredItems.map((item, index) => (
                                        <ListItemButton
                                            key={triggerChar === '#' ? item.offerId : item.hasoffersid}
                                            onClick={() => handleSelectItem(item)}
                                            selected={index === highlightedIndex}
                                            sx={{
                                                '&.Mui-selected': {
                                                    backgroundColor: '#e3f2fd',
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: '#bbdefb',
                                                }
                                            }}
                                        >
                                            <ListItemText
                                                primary={triggerChar === '#' ? item.name : item.fullname}
                                                secondary={`ID: ${triggerChar === '#' ? item.offerId : item.hasoffersid}`}
                                                primaryTypographyProps={{ style: { fontFamily: "'Montserrat', sans-serif" } }}
                                                secondaryTypographyProps={{ style: { fontFamily: "'Montserrat', sans-serif" } }}
                                            />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Paper>
                        </ClickAwayListener>
                    )}

                    <Box sx={{ p: 3, borderTop: '1px solid #eee', bgcolor: '#fafafa' }}>
                        <ComposerPrimitive.Root style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                            <ComposerPrimitive.Input
                                value={inputValue} // Controlled input
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                style={{
                                    flexGrow: 1,
                                    padding: '14px 20px',
                                    borderRadius: '25px',
                                    border: '1px solid #ddd',
                                    outline: 'none',
                                    fontSize: '14px',
                                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                    lineHeight: 1.7,
                                    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)',
                                    backgroundColor: 'white',
                                    cursor: 'text'
                                }} placeholder={"Use @ for influencer, # for offer..."} />
                            <ComposerPrimitive.Send
                                style={{
                                    padding: '12px 24px',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '25px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    fontFamily: "'Montserrat', sans-serif",
                                    transition: 'all 0.2s',
                                    '&:hover': { backgroundColor: readOnly ? '#ccc' : '#1565c0' }
                                }}>Send</ComposerPrimitive.Send>
                        </ComposerPrimitive.Root>
                    </Box>
                </ThreadPrimitive.Root>
            </div>
        </AssistantRuntimeProvider>
    );
};

export default ActiveChatSession;
